import { useState } from "react";
import { normalizeUrl, isValidWebUrl } from "@/lib/utils";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Upload } from "lucide-react";
import { Helmet } from "react-helmet-async";

const COMMITTEE_EMAIL = "info@rcnmo.com";

const referenceSchema = z.object({
  name: z.string().trim().min(1, "Required").max(120),
  phone: z.string().trim().min(7, "Required").max(40),
  email: z.string().trim().email("Valid email required").max(255),
});

const schema = z.object({
  first_name: z.string().trim().min(1).max(80),
  last_name: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(7, "Required").max(40),
  photo_url: z.string().max(500).optional().or(z.literal("")),
  company: z.string().trim().min(1).max(160),
  title: z.string().trim().min(1).max(160),
  category: z.string().trim().min(1, "Required").max(120),
  chapter: z.enum(["washington", "union"], { required_error: "Choose a chapter" }),
  description: z.string().trim().min(10, "At least 10 characters").max(500),
  about: z.string().trim().max(2000).optional().or(z.literal("")),
  website: z.string().trim().max(255).refine(isValidWebUrl, "Please enter a valid web address").optional().or(z.literal("")),
  linkedin: z.string().trim().max(255).refine(isValidWebUrl, "Please enter a valid web address").optional().or(z.literal("")),
  facebook: z.string().trim().max(255).refine(isValidWebUrl, "Please enter a valid web address").optional().or(z.literal("")),

  reference_1: referenceSchema,
  reference_2: referenceSchema,
  reference_3: referenceSchema,

  heard_about: z.string().trim().min(1, "Required").max(500),
  community_organizations: z.string().trim().max(2000).optional().or(z.literal("")),
  volunteer_activities: z.string().trim().max(2000).optional().or(z.literal("")),
  reason_to_join: z.string().trim().min(10, "At least 10 characters").max(2000),
  business_goals: z.string().trim().min(10, "At least 10 characters").max(2000),

  agreed_conduct: z.literal(true, { errorMap: () => ({ message: "Required to apply" }) }),
  agreed_attendance: z.literal(true, { errorMap: () => ({ message: "Required to apply" }) }),
  agreed_background_check: z.literal(true, { errorMap: () => ({ message: "Required to apply" }) }),
});

type FormState = z.input<typeof schema>;

const emptyRef = { name: "", phone: "", email: "" };
const empty: any = {
  first_name: "", last_name: "", email: "", phone: "", photo_url: "",
  company: "", title: "", category: "", chapter: "",
  description: "", about: "", website: "", linkedin: "", facebook: "",
  reference_1: { ...emptyRef }, reference_2: { ...emptyRef }, reference_3: { ...emptyRef },
  heard_about: "", community_organizations: "", volunteer_activities: "",
  reason_to_join: "", business_goals: "",
  agreed_conduct: false, agreed_attendance: false, agreed_background_check: false,
};

const CATEGORIES = [
  "Financial Services", "Construction & Contracting", "Marketing & Advertising",
  "Non-Profit", "Health & Wellness", "Insurance", "Automotive",
  "Legal Services", "Real Estate", "Retail & Food Services",
  "Technology & IT", "Travel & Tourism", "Other",
];

const Apply = () => {
  const [form, setForm] = useState<any>(empty);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);

  const set = (key: string, value: any) => {
    setForm((f: any) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  const setRef = (refKey: "reference_1" | "reference_2" | "reference_3", field: "name" | "phone" | "email", value: string) => {
    setForm((f: any) => ({ ...f, [refKey]: { ...f[refKey], [field]: value } }));
    setErrors((e) => ({ ...e, [`${refKey}.${field}`]: "" }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${Date.now()}-${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("application-photos").upload(path, file);
      if (error) throw error;
      // Store the storage path (not a public URL) — bucket is private; admins view via signed URLs.
      set("photo_url", path);
      setPhotoPreview(URL.createObjectURL(file));
      toast.success("Photo uploaded");
    } catch (err: any) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fieldErrs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrs[issue.path.join(".")] = issue.message;
      }
      setErrors(fieldErrs);
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const d = parsed.data;
      const applicationId = crypto.randomUUID();
      const { error } = await supabase
        .from("applications")
        .insert({
          id: applicationId,
          first_name: d.first_name,
          last_name: d.last_name,
          email: d.email,
          phone: d.phone,
          photo_url: d.photo_url || null,
          company: d.company,
          title: d.title,
          category: d.category,
          chapter: d.chapter,
          description: d.description,
          about: d.about || null,
          website: normalizeUrl(d.website),
          linkedin: normalizeUrl(d.linkedin),
          facebook: normalizeUrl(d.facebook),
          reference_1_name: d.reference_1.name,
          reference_1_phone: d.reference_1.phone,
          reference_1_email: d.reference_1.email,
          reference_2_name: d.reference_2.name,
          reference_2_phone: d.reference_2.phone,
          reference_2_email: d.reference_2.email,
          reference_3_name: d.reference_3.name,
          reference_3_phone: d.reference_3.phone,
          reference_3_email: d.reference_3.email,
          heard_about: d.heard_about,
          community_organizations: d.community_organizations || null,
          volunteer_activities: d.volunteer_activities || null,
          reason_to_join: d.reason_to_join,
          business_goals: d.business_goals,
          agreed_conduct: d.agreed_conduct,
          agreed_attendance: d.agreed_attendance,
          agreed_background_check: d.agreed_background_check,
          status: "submitted",
        });
      if (error) throw error;

      const chapterLabel = d.chapter === "washington" ? "Washington" : "Union";
      const templateData = {
        firstName: d.first_name, lastName: d.last_name,
        email: d.email, phone: d.phone, company: d.company,
        title: d.title, category: d.category, chapter: chapterLabel,
        reasonToJoin: d.reason_to_join,
      };
      void Promise.allSettled([
        supabase.functions.invoke("send-public-transactional-email", {
          body: {
            templateName: "application-committee",
            idempotencyKey: `app-committee-${applicationId}`,
            templateData: { ...templateData, applicationId },
          },
        }),
        supabase.functions.invoke("send-public-transactional-email", {
          body: {
            templateName: "application-confirmation",
            recipientEmail: d.email,
            idempotencyKey: `app-confirm-${applicationId}`,
            templateData: { firstName: d.first_name, chapter: chapterLabel, email: d.email },
          },
        }),
      ]);

      setSubmitted(true);
      setForm(empty);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Apply for Membership | Referral Connection Network</title>
        <meta name="description" content="Apply to join the Referral Connection Network — an industry-exclusive community of business professionals in Washington and Union, MO." />
      </Helmet>
      <Navigation />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Apply for Membership</h1>
              <p className="text-lg text-muted-foreground">
                Join an industry-exclusive community of business professionals committed to building meaningful relationships and giving back.
              </p>
            </div>

            {submitted ? (
              <Card>
                <CardContent className="pt-10 pb-10 text-center">
                  <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
                  <h2 className="text-2xl font-bold mb-2">Application received!</h2>
                  <p className="text-muted-foreground mb-6">
                    Thank you for applying to RCN. Our membership committee will review your application and references, and follow up with you by email within 1–2 weeks.
                  </p>
                  <Button onClick={() => setSubmitted(false)} variant="outline">
                    Submit another application
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* About You */}
                <Card>
                  <CardHeader>
                    <CardTitle>About You</CardTitle>
                    <CardDescription>Tell us who you are and your business.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="First name" error={errors.first_name}>
                        <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                      </Field>
                      <Field label="Last name" error={errors.last_name}>
                        <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Email" error={errors.email}>
                        <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                      </Field>
                      <Field label="Phone" error={errors.phone}>
                        <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                      </Field>
                    </div>
                    <Field label="Headshot or company logo" error={errors.photo_url}>
                      <div className="flex flex-col sm:flex-row items-start gap-3">
                        {photoPreview && (
                          <img src={photoPreview} alt="Uploaded" className="w-24 h-24 object-cover rounded border" />
                        )}
                        <div>
                          <input type="file" accept="image/*" id="photo-upload" className="hidden" onChange={handlePhotoUpload} />
                          <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
                            <label htmlFor="photo-upload" className="cursor-pointer">
                              <Upload className="h-4 w-4 mr-2" />
                              {uploading ? "Uploading..." : form.photo_url ? "Replace photo" : "Upload photo"}
                            </label>
                          </Button>
                        </div>
                      </div>
                    </Field>
                  </CardContent>
                </Card>

                {/* Business */}
                <Card>
                  <CardHeader>
                    <CardTitle>Your Business</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Company" error={errors.company}>
                        <Input value={form.company} onChange={(e) => set("company", e.target.value)} />
                      </Field>
                      <Field label="Your title" error={errors.title}>
                        <Input value={form.title} onChange={(e) => set("title", e.target.value)} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Category / Industry" error={errors.category}>
                        <Select value={form.category} onValueChange={(v) => set("category", v)}>
                          <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Chapter" error={errors.chapter}>
                        <Select value={form.chapter} onValueChange={(v) => set("chapter", v)}>
                          <SelectTrigger><SelectValue placeholder="Select a chapter" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="washington">Washington, MO</SelectItem>
                            <SelectItem value="union">Union, MO</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <Field label="One-line description of what you do" error={errors.description}>
                      <Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Helping homeowners protect their largest investment" />
                    </Field>
                    <Field label="About your business (optional)" error={errors.about}>
                      <Textarea rows={4} value={form.about} onChange={(e) => set("about", e.target.value)} />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Website (optional)" error={errors.website}>
                        <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="example.com" />
                      </Field>
                      <Field label="LinkedIn (optional)" error={errors.linkedin}>
                        <Input value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} />
                      </Field>
                      <Field label="Facebook (optional)" error={errors.facebook}>
                        <Input value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
                      </Field>
                    </div>
                  </CardContent>
                </Card>

                {/* References */}
                <Card>
                  <CardHeader>
                    <CardTitle>References</CardTitle>
                    <CardDescription>Provide three professional references we may contact.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {(["reference_1", "reference_2", "reference_3"] as const).map((key, i) => (
                      <div key={key}>
                        <h4 className="font-semibold mb-3">Reference {i + 1}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <Field label="Name" error={errors[`${key}.name`]}>
                            <Input value={form[key].name} onChange={(e) => setRef(key, "name", e.target.value)} />
                          </Field>
                          <Field label="Phone" error={errors[`${key}.phone`]}>
                            <Input type="tel" value={form[key].phone} onChange={(e) => setRef(key, "phone", e.target.value)} />
                          </Field>
                          <Field label="Email" error={errors[`${key}.email`]}>
                            <Input type="email" value={form[key].email} onChange={(e) => setRef(key, "email", e.target.value)} />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Context */}
                <Card>
                  <CardHeader>
                    <CardTitle>Tell Us More</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <Field label="How did you hear about RCN?" error={errors.heard_about}>
                      <Textarea rows={2} value={form.heard_about} onChange={(e) => set("heard_about", e.target.value)} />
                    </Field>
                    <Field label="Community organizations you're involved with (optional)" error={errors.community_organizations}>
                      <Textarea rows={3} value={form.community_organizations} onChange={(e) => set("community_organizations", e.target.value)} />
                    </Field>
                    <Field label="Volunteer activities (optional)" error={errors.volunteer_activities}>
                      <Textarea rows={3} value={form.volunteer_activities} onChange={(e) => set("volunteer_activities", e.target.value)} />
                    </Field>
                    <Field label="Primary reason you want to join RCN" error={errors.reason_to_join}>
                      <Textarea rows={4} value={form.reason_to_join} onChange={(e) => set("reason_to_join", e.target.value)} />
                    </Field>
                    <Field label="Business goals you hope RCN will help you achieve" error={errors.business_goals}>
                      <Textarea rows={4} value={form.business_goals} onChange={(e) => set("business_goals", e.target.value)} />
                    </Field>
                  </CardContent>
                </Card>

                {/* Agreements */}
                <Card>
                  <CardHeader>
                    <CardTitle>Agreements</CardTitle>
                    <CardDescription>All three are required to submit.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Agreement
                      checked={!!form.agreed_conduct}
                      onChange={(v) => set("agreed_conduct", v)}
                      error={errors.agreed_conduct}
                      id="conduct"
                      label="I agree to conduct myself professionally on social media and the RCN website, representing the organization with integrity at all times."
                    />
                    <Agreement
                      checked={!!form.agreed_attendance}
                      onChange={(v) => set("agreed_attendance", v)}
                      error={errors.agreed_attendance}
                      id="attendance"
                      label="I agree to RCN's attendance and substitution policy, including notifying the chapter when I cannot attend a meeting."
                    />
                    <Agreement
                      checked={!!form.agreed_background_check}
                      onChange={(v) => set("agreed_background_check", v)}
                      error={errors.agreed_background_check}
                      id="background"
                      label="I consent to RCN performing a basic background screening as part of the membership review process."
                    />
                  </CardContent>
                </Card>

                <Button type="submit" disabled={submitting} className="w-full" size="lg">
                  {submitting ? "Submitting..." : "Submit Application"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Field = ({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    {children}
    {error && <p className="text-sm text-destructive">{error}</p>}
  </div>
);

const Agreement = ({ checked, onChange, error, id, label }: { checked: boolean; onChange: (v: boolean) => void; error?: string; id: string; label: string }) => (
  <div className="flex items-start gap-3">
    <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" />
    <div className="space-y-1">
      <Label htmlFor={id} className="cursor-pointer leading-snug font-normal">{label}</Label>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  </div>
);

export default Apply;
